//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React from 'react';
import { Box, Typography, type TypographyProps } from '@mui/material';
import { useTheme, type SxProps, type Theme } from '@mui/material/styles';

export interface BrandedTitleBarProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> {
  title: React.ReactNode;
  // The id the surrounding dialog or panel points `aria-labelledby` at. The
  // bar draws the only heading such a surface has, so it is usually the one
  // thing naming it.
  titleId?: string;
  icon?: React.ReactNode;
  // Sized by the chrome it titles: a dialog carries an `h5` or `h6`, a panel
  // title bar a `subtitle2`. The accent bar follows whatever this comes to.
  variant?: TypographyProps['variant'];
  // Controls belonging to the surface -- close, hide, an options menu --
  // pushed to the trailing edge.
  actions?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * The brand mark at the head of a dialog or a floating panel: the red bar,
 * the title, and the surface's own controls on one line.
 *
 * The same idiom as `HeaderText`, which marks a page heading, sized for
 * chrome instead. The bar stretches to the row rather than taking a fixed
 * height, so one component serves a dialog title and a panel title bar
 * without either dictating the other's type scale.
 *
 * Both ornaments -- the bar and the icon -- are inert to the pointer, so the
 * whole element can be a caller's drag surface: a press anywhere that is not
 * a control reaches the root, which is what a drag guard tests against.
 */
const BrandedTitleBar = ({
  title, titleId, icon, variant = 'h6', actions, sx, ...rest
}: BrandedTitleBarProps) => {
  const theme = useTheme();

  return (
    <Box
      {...rest}
      sx={[{ display: 'flex', alignItems: 'center', gap: 1 },
           ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      <Box
        aria-hidden
        data-testid="branded-title-accent"
        sx={{
          alignSelf: 'stretch',
          flexShrink: 0,
          width: '5px',
          // A minimum for the degenerate row: a bar with nothing tall in it
          // would otherwise collapse to a hairline.
          minHeight: '24px',
          pointerEvents: 'none',
          backgroundColor: theme.palette.header.background
        }}
      />
      {icon && (
        <Box
          aria-hidden
          sx={{ display: 'flex', pointerEvents: 'none' }}
        >
          {icon}
        </Box>
      )}
      <Typography
        id={titleId}
        // A label for the surface, not a section heading: the heading levels
        // MUI's title variants imply would land after the page's h1 and skip
        // several on the way.
        component="span"
        variant={variant}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {title}
      </Typography>
      {actions}
    </Box>
  );
};

export default BrandedTitleBar;
